import { Controller } from '@hotwired/stimulus'

const MINIMUM_THINKING_TIME_MS = 1400
// Cada cuánto se le pregunta al servidor si el turno ya está cerrado. Por debajo de un
// segundo el sondeo cuesta más que la propia respuesta; por encima de tres, la espera
// se nota aunque el asistente haya contestado.
const POLL_INTERVAL_MS = 1500
// Tope de espera antes de rendirse. Da margen a los 60 s como mucho que puede tardar
// OpenAI (OPENAI_CHAT_READ_TIMEOUT) más el tiempo en cola por delante.
const MAX_WAIT_MS = 120000

const STATUS_READY = 'ready'
const STATUS_FAILED = 'failed'

export default class extends Controller {
  static targets = ['form', 'input', 'messages', 'submit', 'conversation']
  static values = {
    errorMessage: String,
    thinkingMessage: String,
    timeoutMessage: String,
    youLabel: String,
    assistantLabel: String
  }

  connect() {
    this.busy = false
    this.abandoned = false
    this.scrollToLatest()
  }

  // Al navegar a otra página el sondeo tiene que parar: sin esto seguiría pidiendo
  // hasta agotar el tope sobre una vista que ya no existe.
  disconnect() {
    this.abandoned = true
  }

  keydown(event) {
    if(event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.formTarget.requestSubmit()
    }
  }

  async submit(event) {
    event.preventDefault()
    const message = this.inputTarget.value.trim()
    if(this.busy || message.length === 0) return

    this.busy = true
    this.submitTarget.disabled = true
    this.inputTarget.disabled = true
    const userMessage = this.appendMessage('user', message)
    const thinkingStartedAt = Date.now()
    const thinkingMessage = this.appendThinkingMessage()
    this.inputTarget.value = ''

    try {
      // El servidor sólo guarda la pregunta y contesta 202 con dónde consultar: la
      // llamada al modelo la hace un worker, fuera de esta petición.
      const accepted = await this.postQuestion(message)
      // La hora la pone el servidor, que es quien conoce el idioma: el globo se pinta al
      // enviar y su marca se rellena en cuanto llega el acuse.
      this.setMessageTime(userMessage, accepted.asked_at)
      const answer = await this.waitForAnswer(accepted.status_url)
      await this.waitForMinimumThinkingTime(thinkingStartedAt)
      thinkingMessage.remove()

      this.conversationTarget.value = accepted.conversation_id
      this.appendMessage('assistant', answer.answer, answer.answered_at)
      // La conversación sólo se fija en la URL cuando hay respuesta: si el turno falla,
      // el servidor puede haber retirado un hilo que nació con esa pregunta, y volver
      // luego a un identificador que ya no existe daría un error sin sentido.
      this.rememberConversation(accepted.conversation_id)
    } catch(error) {
      await this.waitForMinimumThinkingTime(thinkingStartedAt)
      thinkingMessage.remove()
      // Al fallar se deshace el envío: la pregunta vuelve al campo y desaparece del hilo,
      // que solo debe guardar lo que llegó a contestarse. Errores como el de asistente
      // ocupado piden reintentar, y reescribirlo entero sería un castigo de más.
      userMessage.remove()
      this.inputTarget.value = message
      this.appendMessage('error', error.chatMessage || this.errorMessageValue)
    } finally {
      this.busy = false
      this.submitTarget.disabled = false
      this.inputTarget.disabled = false
      this.inputTarget.focus()
    }
  }

  async postQuestion(message) {
    const response = await fetch(this.formTarget.action, {
      method: 'POST',
      credentials: 'same-origin',
      headers: this.requestHeaders(),
      body: JSON.stringify({
        message: message,
        conversation_id: this.conversationTarget.value
      })
    })
    const payload = await this.responsePayload(response)
    if(!response.ok) throw this.chatError(payload.error)
    if(!payload.status_url || !payload.conversation_id) throw this.chatError()

    return payload
  }

  async waitForAnswer(statusUrl) {
    const deadline = Date.now() + MAX_WAIT_MS

    while(Date.now() < deadline) {
      await this.wait(POLL_INTERVAL_MS)
      if(this.abandoned) throw this.chatError()

      const response = await fetch(statusUrl, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      })
      const payload = await this.responsePayload(response)
      if(!response.ok) throw this.chatError(payload.error)
      if(payload.status === STATUS_FAILED) throw this.chatError(payload.error)
      if(payload.status === STATUS_READY && typeof payload.answer === 'string') return payload
    }

    throw this.chatError(this.timeoutMessageValue)
  }

  requestHeaders() {
    return {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
    }
  }

  chatError(message) {
    const error = new Error('Chat request failed')
    error.chatMessage = message || this.errorMessageValue
    return error
  }

  wait(milliseconds) {
    return new Promise(resolve => window.setTimeout(resolve, milliseconds))
  }

  waitForMinimumThinkingTime(startedAt) {
    return this.wait(Math.max(0, MINIMUM_THINKING_TIME_MS - (Date.now() - startedAt)))
  }

  appendThinkingMessage() {
    const article = document.createElement('article')
    article.className = 'aim-chat-message assistant thinking'
    article.setAttribute('role', 'status')

    const clock = document.createElement('span')
    clock.className = 'aim-chat-thinking-clock'
    clock.setAttribute('aria-hidden', 'true')

    const text = document.createElement('span')
    text.textContent = this.thinkingMessageValue
    article.append(clock, text)
    this.messagesTarget.appendChild(article)
    this.scrollToLatest()
    return article
  }

  async responsePayload(response) {
    const contentType = response.headers.get('content-type') || ''
    if(!contentType.includes('application/json')) return {}

    try {
      return await response.json()
    } catch(_error) {
      return {}
    }
  }

  // Los globos que pinta el navegador tienen que salir iguales que los que ya venían
  // montados desde el servidor: con quién habla y a qué hora. Antes sólo llevaban el
  // texto, así que el último mensaje de cada turno se veía distinto al recargar.
  appendMessage(role, content, timestamp) {
    const article = document.createElement('article')
    article.className = `aim-chat-message ${role}`
    if(role !== 'error') article.appendChild(this.messageHead(role, timestamp))

    const paragraph = document.createElement('p')
    paragraph.textContent = content
    article.appendChild(paragraph)
    this.messagesTarget.appendChild(article)
    this.scrollToLatest()
    return article
  }

  messageHead(role, timestamp) {
    const head = document.createElement('span')
    head.className = 'aim-chat-message-role'
    head.textContent = role === 'user' ? this.youLabelValue : this.assistantLabelValue

    const time = document.createElement('time')
    time.className = 'aim-chat-message-time'
    if(timestamp) time.textContent = timestamp
    head.appendChild(time)
    return head
  }

  setMessageTime(article, timestamp) {
    if(!timestamp) return

    const time = article.querySelector('.aim-chat-message-time')
    if(time) time.textContent = timestamp
  }

  scrollToLatest() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  rememberConversation(id) {
    const url = new URL(window.location.href)
    url.searchParams.set('conversation_id', id)
    window.history.replaceState({}, '', url.toString())
  }
}
